// AIDEV-NOTE: Pagina de detalhe do pedido — busca por ID via TanStack Query
// Exibe dados principais, itens, parcelas e permite compartilhamento via ShareModal
// Integra Supabase Realtime (usePedidoRealtime) e SyncStatusBadge (TASK-080/081/082)

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Stack,
  IconButton
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplayIcon from '@mui/icons-material/Replay';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { Pedido } from '@azoupvendas/shared';
import { podeExcluirPedido } from '@azoupvendas/shared';
import { useAuth } from '../../../hooks/useAuth';
import { ShareModal } from '../components/ShareModal';
import { SyncStatusBadge } from '../components/SyncStatusBadge';
import { usePedidoRealtime } from '../hooks/usePedidoRealtime';
import api from '../../../services/auth.service';
import { pedidoClientService } from '../services/pedido.service';
import { ExcluirPedidoDialog } from '../components/ExcluirPedidoDialog';

// -- Helpers --

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}

// AIDEV-NOTE: Strings date-only (YYYY-MM-DD) do Firebird nao passam por UTC para evitar -1 dia em UTC-3
function formatDate(dateStr: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const parts = dateStr.split('-');
    const y = parseInt(parts[0] ?? '0', 10);
    const m = parseInt(parts[1] ?? '1', 10);
    const d = parseInt(parts[2] ?? '1', 10);
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
  }
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

// AIDEV-NOTE: Labels e cores do status ERP (CARTEIRA_PEDIDO.STATUS do Firebird)
const STATUS_ERP_LABELS: Record<string, string> = {
  P: 'ERP: Em análise',
  A: 'ERP: Aprovado',
  R: 'ERP: Romaneado',
  F: 'ERP: Faturado'
};
const STATUS_ERP_COLORS: Record<string, 'default' | 'warning' | 'success' | 'info' | 'primary'> = {
  P: 'warning',
  A: 'success',
  R: 'info',
  F: 'primary'
};

// -- API fetch --

// AIDEV-NOTE: passa pelo service (nao api.get direto) para herdar o fallback Dexie. Pedido
// fechado sem sinal existe so no aparelho ate o drain rodar — sem isto a tela de detalhe abria
// em erro logo apos finalizar offline, e o vendedor concluia que tinha perdido o pedido.
async function fetchPedido(id: string, vendedorId: number): Promise<Pedido> {
  const pedido = await pedidoClientService.buscarPedido(id, vendedorId);
  if (!pedido) throw new Error('Pedido nao encontrado');
  return pedido;
}

// -- Component --

export function PedidoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [shareOpen, setShareOpen] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // AIDEV-NOTE: Supabase Realtime — escuta mudancas de status em tempo real (TASK-080)
  usePedidoRealtime(id);

  const {
    data: pedido,
    isLoading,
    isError,
    error
  } = useQuery<Pedido>({
    queryKey: ['pedido', id],
    queryFn: () => fetchPedido(id!, user?.vendedor_id ?? 0),
    enabled: !!id
  });

  // AIDEV-NOTE: Retry de sync para pedidos com erro_sync (TASK-082)
  // Chama POST /api/sync/pedido/:id e invalida a query para refetch
  const handleRetrySync = useCallback(async () => {
    if (!id) return;

    setIsRetrying(true);
    setRetryError(null);

    try {
      await api.post(`/sync/pedido/${id}`);

      // Invalida queries para forcar refetch com novo status
      void queryClient.invalidateQueries({ queryKey: ['pedido', id] });
      void queryClient.invalidateQueries({ queryKey: ['pedidos'] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao tentar sincronizar novamente';
      setRetryError(message);
    } finally {
      setIsRetrying(false);
    }
  }, [id, queryClient]);

  // AIDEV-NOTE: Exclusao offline-first de rascunho — remove caches e volta para a lista
  const handleConfirmDelete = useCallback(async () => {
    if (!id) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await pedidoClientService.excluirPedido(id, user?.vendedor_id ?? 0);
      queryClient.removeQueries({ queryKey: ['pedido', id] });
      void queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      navigate('/pedidos');
    } catch (err) {
      console.error('Erro ao excluir pedido:', err);
      setDeleteError('Não foi possível excluir o pedido. Tente novamente.');
      setIsDeleting(false);
      setConfirmDeleteOpen(false);
    }
  }, [id, user?.vendedor_id, queryClient, navigate]);

  // -- Loading state --
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // -- Error state --
  if (isError || !pedido) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Erro ao carregar pedido'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/pedidos')}>
          Voltar
        </Button>
      </Box>
    );
  }

  const vendedorData = { nome: user?.nome ?? 'Vendedor' };

  // AIDEV-NOTE: Dados de empresa serao preenchidos futuramente via configuracoes do usuario
  // Por ora, usamos valores parciais que o PDF trata com fallback
  const empresaData = {
    nome_fantasia: 'AzoupVendas',
    cnpj: ''
  };

  // AIDEV-NOTE: Badge "OFFLINE" — pedido criado offline ainda nao sincronizado (TASK-079)
  const isOfflinePedido = pedido.numero == null && pedido.status === 'rascunho';
  // AIDEV-NOTE: rascunho + finalizado/erro_sync sem numero — regra unica em @shared,
  // a mesma que a guarda do servidor aplica (pedidos.repository.deletePedido)
  const canDelete = podeExcluirPedido(pedido);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 960, mx: 'auto' }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
      >
        <IconButton onClick={() => navigate('/pedidos')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Pedido {pedido.numero ? `N. ${pedido.numero}` : '(Rascunho)'}
        </Typography>

        {/* AIDEV-NOTE: Badge OFFLINE para pedidos nao sincronizados (TASK-079) */}
        {isOfflinePedido && (
          <Chip
            label="OFFLINE"
            size="small"
            variant="outlined"
            color="default"
            sx={{ fontWeight: 'bold', mr: 1 }}
          />
        )}

        {/* AIDEV-NOTE: SyncStatusBadge substitui o Chip simples anterior (TASK-081) */}
        <SyncStatusBadge status={pedido.status} syncError={pedido.sync_error} />

        {/* AIDEV-NOTE: Status ERP sincronizado de volta do Firebird (P/A/R/F) */}
        {pedido.status_erp && (
          <Chip
            label={STATUS_ERP_LABELS[pedido.status_erp] ?? `ERP: ${pedido.status_erp}`}
            color={STATUS_ERP_COLORS[pedido.status_erp] ?? 'default'}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      {/* Dados principais */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Dados do Pedido
        </Typography>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Cliente
              </Typography>
              <Typography variant="body2">{pedido.cliente_nome}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                CPF/CNPJ
              </Typography>
              <Typography variant="body2">{pedido.cliente_cpf_cnpj}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Data
              </Typography>
              <Typography variant="body2">
                {pedido.created_at ? formatDate(pedido.created_at) : '--'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="primary">
                {formatCurrency(pedido.total_final)}
              </Typography>
            </Box>
          </Box>
          {pedido.observacao && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Observacao
              </Typography>
              <Typography variant="body2">{pedido.observacao}</Typography>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Itens */}
      <Paper sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ p: 2, pb: 0 }}>
          Itens ({pedido.itens.length})
        </Typography>
        {(() => {
          // AIDEV-NOTE: Colunas dinamicas — exibidas apenas quando algum item tem o campo preenchido
          const hasRomaneada = pedido.itens.some((i) => (i as any).quantidade_romaneada != null);
          const hasFaturada = pedido.itens.some((i) => (i as any).quantidade_faturada != null);
          const usaGrade = pedido.itens.some((i) => (i.codcor ?? 0) > 0 || (i.codtamanho ?? 0) > 0);
          // colSpan: Produto + grade? + Qtd + dinamicos + Vlr Unit + Desc% + Total
          const totalSpan =
            2 + (usaGrade ? 2 : 0) + (hasRomaneada ? 1 : 0) + (hasFaturada ? 1 : 0) + 3;
          // AIDEV-NOTE: overflowX auto — tabela de itens pode ter muitas colunas no mobile
          return (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    {usaGrade && <TableCell>Cor</TableCell>}
                    {usaGrade && <TableCell>Tam</TableCell>}
                    <TableCell align="right">Qtd</TableCell>
                    {hasRomaneada && <TableCell align="right">Rom.</TableCell>}
                    {hasFaturada && <TableCell align="right">Fat.</TableCell>}
                    <TableCell align="right">Vlr Unit</TableCell>
                    <TableCell align="right">Desc%</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pedido.itens.map((item, idx) => {
                    const qtdRomaneada = (item as any).quantidade_romaneada as
                      | number
                      | null
                      | undefined;
                    const qtdFaturada = (item as any).quantidade_faturada as
                      | number
                      | null
                      | undefined;
                    return (
                      <TableRow key={item.id ?? `item-${idx}`}>
                        <TableCell>{item.descricao}</TableCell>
                        {usaGrade && <TableCell>{item.cor ?? '--'}</TableCell>}
                        {usaGrade && <TableCell>{item.tamanho ?? '--'}</TableCell>}
                        <TableCell align="right">{item.quantidade}</TableCell>
                        {hasRomaneada && (
                          <TableCell align="right">
                            {qtdRomaneada != null ? (
                              <Box
                                component="span"
                                sx={{
                                  color: qtdRomaneada < item.quantidade ? 'warning.main' : 'inherit'
                                }}
                              >
                                {qtdRomaneada}
                                {qtdRomaneada < item.quantidade ? ' ⚠' : ''}
                              </Box>
                            ) : (
                              '--'
                            )}
                          </TableCell>
                        )}
                        {hasFaturada && (
                          <TableCell align="right">
                            {qtdFaturada != null ? (
                              <Box
                                component="span"
                                sx={{
                                  color: qtdFaturada < item.quantidade ? 'warning.main' : 'inherit'
                                }}
                              >
                                {qtdFaturada}
                                {qtdFaturada < item.quantidade ? ' ⚠' : ''}
                              </Box>
                            ) : (
                              '--'
                            )}
                          </TableCell>
                        )}
                        <TableCell align="right">{formatCurrency(item.valor_unitario)}</TableCell>
                        <TableCell align="right">
                          {item.desconto_percentual > 0 ? `${item.desconto_percentual}%` : '--'}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(item.total_item)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Linha de totais */}
                  <TableRow>
                    <TableCell colSpan={totalSpan} />
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        Total:
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {formatCurrency(pedido.total_final)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          );
        })()}
      </Paper>

      {/* Parcelas */}
      {pedido.parcelas.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ p: 2, pb: 0 }}>
            Parcelas ({pedido.parcelas.length})
          </Typography>
          <List dense disablePadding>
            {pedido.parcelas.map((p, idx) => (
              <Box key={`parcela-${p.numero}`}>
                {idx > 0 && <Divider />}
                <ListItem>
                  <ListItemText
                    primary={`Parcela ${p.numero}`}
                    secondary={`Vencimento: ${formatDate(p.vencimento)}`}
                  />
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(p.valor)}
                  </Typography>
                </ListItem>
              </Box>
            ))}
          </List>
        </Paper>
      )}

      {/* Sync error info */}
      {pedido.status === 'erro_sync' && pedido.sync_error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            Erro de sincronizacao
          </Typography>
          <Typography variant="body2">{pedido.sync_error}</Typography>
        </Alert>
      )}

      {/* Retry error feedback */}
      {retryError && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setRetryError(null)}>
          {retryError}
        </Alert>
      )}

      {/* Delete error feedback */}
      {deleteError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}

      {/* Actions */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
        {canDelete && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setConfirmDeleteOpen(true)}
          >
            {pedido.status === 'rascunho' ? 'Excluir rascunho' : 'Excluir pedido'}
          </Button>
        )}
        {pedido.status === 'rascunho' && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/pedidos/${pedido.id}/editar`)}
          >
            Editar
          </Button>
        )}
        {pedido.status === 'erro_sync' && (
          <Button
            variant="outlined"
            color="error"
            startIcon={isRetrying ? <CircularProgress size={18} /> : <ReplayIcon />}
            onClick={handleRetrySync}
            disabled={isRetrying}
          >
            {isRetrying ? 'Sincronizando...' : 'Tentar novamente'}
          </Button>
        )}
        <Button variant="contained" startIcon={<ShareIcon />} onClick={() => setShareOpen(true)}>
          Compartilhar
        </Button>
      </Stack>

      {/* Confirmação de exclusão — rascunho ou pedido finalizado ainda não sincronizado */}
      <ExcluirPedidoDialog
        open={confirmDeleteOpen}
        loading={isDeleting}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        pedidoFinalizado={pedido.status !== 'rascunho'}
      />

      {/* Share Modal */}
      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        pedido={pedido}
        empresa={empresaData}
        vendedor={vendedorData}
      />
    </Box>
  );
}
